/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import { css } from '@compiled/react';
import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useConfluencePageAri } from '@atlassian/ai-summary';
import { usePopupDwellTime } from '@atlassian/popup-dwell-time';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AIStreamLoading, AIBorder, AISummaryLoading } from '@atlassian/ai-components';
import { ConvoAIResponseState, useConvoAIStreaming } from '@atlassian/ai-agent-streaming';
import type { ConvoAIStreamingState } from '@atlassian/ai-agent-streaming';
import type {
	AiFeatureRequestEnvelopeContentCatchupRequest,
	ContentCatchupFinalResponseMessage,
	ContentCatchupErrorResponseMessage,
} from '@atlassian/convo-ai-api';

import type { SummaryAnalyticsPropertiesType } from '@confluence/quick-summary';
import { SmartsSummaryErrorComponent } from '@confluence/quick-summary';
import { useSessionData } from '@confluence/session-data';
import { ErrorDisplay } from '@confluence/error-boundary';
import {
	AI_CONTENT_CATCHUP_EXPERIENCE,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { createSingleQueryParamHook } from '@confluence/route-manager';

import type {
	PageCatchupWrapperComponentPageCatchupVersionDiffMetadataForContentQuery as PageCatchupWrapperComponentPageCatchupVersionDiffMetadataForContentQuery$data,
	PageCatchupWrapperComponentPageCatchupVersionDiffMetadataForContentQueryVariables as PageCatchupWrapperComponentPageCatchupVersionDiffMetadataForContentQuery$variables,
} from '../__types__/PageCatchupWrapperComponentPageCatchupVersionDiffMetadataForContentQuery';
import type {
	PageCatchupWrapperComponentPageCatchupMetadataQuery as PageCatchupWrapperComponentPageCatchupMetadataQuery$data,
	PageCatchupWrapperComponentPageCatchupMetadataQueryVariables as PageCatchupWrapperComponentPageCatchupMetadataQuery$variables,
} from '../__types__/PageCatchupWrapperComponentPageCatchupMetadataQuery';
import { PageCatchupComponent } from '../platform-ui/PageCatchupComponent';
import { CatchupContentType } from '../__types__/PageCatchupMetadataQuery';
import { mapToEditors } from '../data/mapToEditors';
import { mapToFormattedDate } from '../data/mapToFormattedDate';
import { AI_CONTENT_CATCHUP_INTERACTION_METRIC } from '../perf.config';
import { useGetVersionHistoryLink } from '../useGetVersionHistoryLink';
import { buildCatchupRequest } from '../data/buildCatchupRequest';

import type { PageCatchupWrapperComponentProps } from './PageCatchupStreamingEntryPoint';

const PageCatchupVersionDiffWrapperComponentPageCatchupMetadataQuery = gql`
	query PageCatchupVersionDiffWrapperComponentPageCatchupMetadataQuery(
		$contentId: ID!
		$contentType: CatchupContentType!
		$endTimeMs: Long!
	) {
		catchupEditMetadataForContent(
			contentId: $contentId
			contentType: $contentType
			endTimeMs: $endTimeMs
		) {
			lastVisitTimeISO
			hasVersionChangedSinceLastVisit
			users {
				displayName
				profilePicture {
					path
				}
			}
		}
		content(id: $contentId) {
			nodes {
				version {
					number
				}
			}
		}
	}
`;
const PageCatchupVersionDiffWrapperComponentPageCatchupVersionDiffMetadataForContentQuery = gql`
	query PageCatchupVersionDiffWrapperComponentPageCatchupVersionDiffMetadataForContentQuery(
		$contentId: ID!
		$contentType: CatchupContentType!
		$originalContentVersion: Int!
		$revisedContentVersion: Int!
	) {
		catchupVersionDiffMetadataForContent(
			contentId: $contentId
			contentType: $contentType
			originalContentVersion: $originalContentVersion
			revisedContentVersion: $revisedContentVersion
		) {
			isDiffEmpty
			users {
				displayName
				profilePicture {
					height
					isDefault
					width
					path
				}
			}
		}
	}
`;
const useOpenContentCatchupParam = createSingleQueryParamHook('openContentCatchup');

const styles = css({
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/design-system/use-tokens-typography
	h3: { fontSize: `${token('font.heading.small')} !important` },
});

export const PageCatchupVersionDiffWrapperComponent: FC<PageCatchupWrapperComponentProps> = ({
	contentId,
	contentType,
	spaceKey,
	source,
	selectedVersions,
	isInObjectSidebar,
}) => {
	const { locale } = useIntl();
	const { cloudId } = useSessionData();
	const { pageAri } = useConfluencePageAri(cloudId, contentId, contentType);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [aiInteractionId] = useState(uuid());
	const experienceTracker = useContext(ExperienceTrackerContext);
	const catchUpContentRef = useRef<HTMLDivElement | null>(null);
	const { getDwellTime } = usePopupDwellTime(catchUpContentRef);
	const [dateInMs] = useState(Date.now());
	const [hideMetadata, setHideMetadata] = useState(true);

	const analyticsProperties: SummaryAnalyticsPropertiesType = {
		source: 'pageCatchup',
		additionalAnalyticsAttributes: {
			subSource: source,
		},
	};

	const originalContentVersion = selectedVersions ? Math.min(...Array.from(selectedVersions)) : 0;
	const revisedContentVersion = selectedVersions ? Math.max(...Array.from(selectedVersions)) : 0;
	const hasSelectedVersions =
		selectedVersions && selectedVersions.size > 1 && source === 'pageHistory';

	const isStreamingDone = useRef(false);
	const isStreamInitializationDone = useRef(false);
	const timeToFirstAnswerPart = useRef(0);

	const {
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultError,
		trackAIResultAction,
		trackAIInteractionDismiss,
	} = useAIEventsInstrumentation();

	const hasQueryParamOpenSet = useOpenContentCatchupParam() === 'true';

	const contentTypeEnum =
		contentType === 'page' ? CatchupContentType.PAGE : CatchupContentType.BLOGPOST;

	const { data, error: defaultMetaDataError } = useQuery<
		PageCatchupWrapperComponentPageCatchupMetadataQuery$data,
		PageCatchupWrapperComponentPageCatchupMetadataQuery$variables
	>(PageCatchupVersionDiffWrapperComponentPageCatchupMetadataQuery, {
		variables: { contentId, contentType: contentTypeEnum, endTimeMs: dateInMs },
		skip: hasSelectedVersions,
	});
	const { data: versionDiffMetadata, error: versionDiffMetadataError } = useQuery<
		PageCatchupWrapperComponentPageCatchupVersionDiffMetadataForContentQuery$data,
		PageCatchupWrapperComponentPageCatchupVersionDiffMetadataForContentQuery$variables
	>(PageCatchupVersionDiffWrapperComponentPageCatchupVersionDiffMetadataForContentQuery, {
		variables: {
			contentId,
			contentType: contentTypeEnum,
			originalContentVersion,
			revisedContentVersion,
		},
		skip: !hasSelectedVersions,
	});
	const metaDataError = defaultMetaDataError || versionDiffMetadataError;

	const isVersionDiffEmpty =
		versionDiffMetadata?.catchupVersionDiffMetadataForContent?.isDiffEmpty ?? false;

	const versionDiffUsers = versionDiffMetadata?.catchupVersionDiffMetadataForContent?.users ?? [];
	const versionDiffEditors = mapToEditors(versionDiffUsers);

	const setIsStreamingDone = () => {
		isStreamingDone.current = true;
	};

	const setIsStreamInitializationDone = () => {
		isStreamInitializationDone.current = true;
	};

	const onShowMoreClick = useCallback(() => {
		trackAIResultAction('showMoreClicked');
	}, [trackAIResultAction]);

	const [request, config] = useMemo(
		() =>
			buildCatchupRequest({
				contentAri: pageAri ?? '',
				locale,
				updateType: 'BETWEEN_VERSIONS',
				endTimeMs: dateInMs,
				selectedVersions,
			}),
		[pageAri, locale, dateInMs, selectedVersions],
	);

	const onStateChange = useCallback(
		(
			state: ConvoAIStreamingState<
				ContentCatchupFinalResponseMessage,
				ContentCatchupErrorResponseMessage
			>,
		) => {
			switch (state.responseState) {
				case ConvoAIResponseState.Initialization:
					if (state.content === null) {
						experienceTracker.start({
							name: AI_CONTENT_CATCHUP_EXPERIENCE,
						});
						AI_CONTENT_CATCHUP_INTERACTION_METRIC.start();
						trackAIInteractionInit();
						timeToFirstAnswerPart.current = Date.now();
					}
					break;
				case ConvoAIResponseState.Error:
					const { status_code, message_template } = state.error.message;
					AI_CONTENT_CATCHUP_INTERACTION_METRIC.stop();
					trackAIResultError({
						aiErrorCode: status_code,
						aiErrorMessage: message_template,
					});
					setIsStreamingDone();
					break;
				case ConvoAIResponseState.AnswerPart:
					if (!isStreamInitializationDone.current) {
						setIsStreamInitializationDone();

						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'streamReceived',
								actionSubject: 'pageCatchup',
								source: source ?? 'page',
								actionSubjectId: 'pageCatchupDialog',
								attributes: {
									contentId,
									timeToFirstAnswerPartInMs: Date.now() - timeToFirstAnswerPart.current,
								},
							},
						}).fire();
					}
					break;
				case ConvoAIResponseState.FinalResponse:
					AI_CONTENT_CATCHUP_INTERACTION_METRIC.stop();
					createAnalyticsEvent({
						type: 'sendScreenEvent',
						data: {
							name: 'pageCatchupDialog',
							attributes: {
								contentId,
								aiInteractionId,
								hasQueryParamOpenSet,
							},
						},
					}).fire();
					trackAIResultView();
					setIsStreamingDone();
					break;
				default:
					break;
			}
		},
		[
			trackAIResultError,
			createAnalyticsEvent,
			contentId,
			aiInteractionId,
			hasQueryParamOpenSet,
			trackAIResultView,
			experienceTracker,
			trackAIInteractionInit,
			source,
		],
	);

	const [{ content, responseState, error: streamSummaryError }] = useConvoAIStreaming<
		AiFeatureRequestEnvelopeContentCatchupRequest,
		ContentCatchupFinalResponseMessage,
		ContentCatchupErrorResponseMessage
	>(request, config, onStateChange);

	useEffect(() => {
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'dwelled',
					actionSubject: 'pageCatchup',
					source: source ?? 'page',
					actionSubjectId: 'pageCatchupDialog',
					attributes: {
						contentId,
						dwellTime: getDwellTime(),
						aiInteractionId,
						hasQueryParamOpenSet,
					},
				},
			}).fire();

			if (!isStreamInitializationDone.current) {
				trackAIInteractionDismiss({ attributes: { dismissTiming: 'stream_initialization' } });
			} else if (!isStreamingDone.current) {
				trackAIInteractionDismiss({ attributes: { dismissTiming: 'answer_part_streaming' } });
			}
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (responseState === ConvoAIResponseState.FinalResponse) {
			if (hideMetadata) setHideMetadata(false);
		}
	}, [responseState, hideMetadata]);

	const lastVisitedOnDate = mapToFormattedDate(
		data?.catchupEditMetadataForContent?.lastVisitTimeISO,
		locale,
	);

	const hasVersionChangedSinceLastVisit =
		data?.catchupEditMetadataForContent?.hasVersionChangedSinceLastVisit ?? false;

	const users = data?.catchupEditMetadataForContent?.users ?? [];

	const defaultEditors = mapToEditors(users);

	const editors = source === 'pageHistory' ? versionDiffEditors : defaultEditors;

	const pageVersion = data?.content?.nodes?.[0]?.version?.number ?? -1;

	const { versionHistoryLink } = useGetVersionHistoryLink(contentId, spaceKey, pageVersion);

	const onVersionHistoryLinkClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'pageCatchup',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'pageCatchupVersionHistoryLink',
				attributes: {
					contentId,
					aiInteractionId,
					hasQueryParamOpenSet,
				},
			},
		}).fire();
		trackAIResultAction('versionHistoryLinkClick');
	}, [contentId, createAnalyticsEvent, aiInteractionId, hasQueryParamOpenSet, trackAIResultAction]);

	const pageCatchupComponentProps = {
		contentId,
		contentType,
		spaceKey,
		lastVisitedOnDate,
		editors,
		ref: catchUpContentRef,
		hideMetadata,
		versionHistoryLink,
		onVersionHistoryLinkClick,
		hasVersionChangedSinceLastVisit,
		onShowMoreClick,
		source,
		isVersionDiffEmpty,
		isInObjectSidebar,
	};

	const renderPageCatchupComponent = (contentToRender: string) => (
		<PageCatchupComponent {...pageCatchupComponentProps} content={contentToRender} />
	);

	const renderContent = () => {
		switch (responseState) {
			case ConvoAIResponseState.Initialization:
				return isInObjectSidebar ? (
					<Box paddingInline="space.100">
						<AISummaryLoading />
					</Box>
				) : (
					<AIStreamLoading />
				);
			case ConvoAIResponseState.Error:
				return (
					<SmartsSummaryErrorComponent
						analyticsProperties={analyticsProperties}
						errorFromApolloOrStream={streamSummaryError}
						contentId={contentId}
						experienceName={AI_CONTENT_CATCHUP_EXPERIENCE}
					/>
				);
			case ConvoAIResponseState.AnswerPart:
			case ConvoAIResponseState.FinalResponse:
				return renderPageCatchupComponent(content as string);
			default:
				break;
		}
	};

	return (
		<div css={styles}>
			{isInObjectSidebar ? (
				<>
					{metaDataError && <ErrorDisplay error={metaDataError} />}
					{renderContent()}
					{responseState === ConvoAIResponseState.FinalResponse && (
						<ExperienceSuccess name={AI_CONTENT_CATCHUP_EXPERIENCE} attributes={{ source }} />
					)}
				</>
			) : (
				<AIBorder loading={responseState === ConvoAIResponseState.Initialization}>
					{metaDataError && <ErrorDisplay error={metaDataError} />}
					{renderContent()}
					{responseState === ConvoAIResponseState.FinalResponse && (
						<ExperienceSuccess name={AI_CONTENT_CATCHUP_EXPERIENCE} attributes={{ source }} />
					)}
				</AIBorder>
			)}
		</div>
	);
};
